import React, { useCallback, useEffect, useState } from 'react';
import {
  Wallet, CreditCard, Calendar, CheckCircle2, Clock, Pencil, Save, X, Lock,
} from 'lucide-react';
import { toast } from 'sonner';

import { api } from '../lib/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import PaymentPlanEditor from './PaymentPlanEditor';
import { useAuth } from '../lib/auth-context';

function formatEUR(n) {
  return `€ ${Number(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function shortDate(iso) {
  if (!iso) return '—';
  const d = (iso || '').slice(0, 10);
  const [y, m, dd] = d.split('-');
  if (!y || !m || !dd) return d;
  return `${dd}/${m}/${y}`;
}

/**
 * PaymentPlanCard — Visualizza il piano pagamenti del lead.
 *
 * Modalità:
 *   • Vista: tabella rate + totali + stato pagamento
 *   • Modifica (solo admin OR agente coinvolto): apre PaymentPlanEditor inline
 *
 * Solo admin può cliccare "Pagata" per marcare una rata come incassata.
 */
export default function PaymentPlanCard({ leadId, leadStage, onUpdated }) {
  const { user } = useAuth() || {};
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const isAdmin = ['admin', 'amministrativo'].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/leads/${leadId}/payment-plan`);
      setPlan(r.data?.plan || null);
    } catch (e) {
      setPlan(null);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { if (leadId) load(); }, [leadId, load]);

  const startEdit = () => {
    if (plan) {
      setDraft({
        payment_type: plan.payment_type,
        currency: plan.currency || 'EUR',
        installments: plan.installments.map((i) => ({
          amount: String(i.amount || ''),
          due_date: (i.due_date || '').slice(0, 10),
          notes: i.notes || '',
        })),
      });
    } else {
      setDraft({
        payment_type: 'secco',
        currency: 'EUR',
        installments: [{ amount: '', due_date: new Date().toISOString().slice(0, 10), notes: 'Anticipo' }],
      });
    }
    setEditing(true);
  };

  const cancelEdit = () => { setDraft(null); setEditing(false); };

  const saveDraft = async () => {
    if (!draft) return;
    const installments = (draft.installments || []).filter(
      (it) => parseFloat(it.amount) > 0 && it.due_date,
    );
    if (installments.length === 0) {
      toast.error('Inserisci almeno una rata con importo e data');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/leads/${leadId}/payment-plan`, {
        payment_type: draft.payment_type,
        currency: draft.currency || 'EUR',
        installments: installments.map((it) => ({
          amount: parseFloat(it.amount),
          due_date: it.due_date,
          notes: it.notes || null,
        })),
      });
      toast.success('Piano pagamenti aggiornato');
      setEditing(false);
      setDraft(null);
      await load();
      if (onUpdated) onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore salvataggio piano');
    } finally {
      setSaving(false);
    }
  };

  const markPaid = async (installmentId) => {
    if (!isAdmin) return;
    try {
      await api.post(`/installments/${installmentId}/mark-paid`);
      toast.success('Rata marcata come pagata');
      await load();
      if (onUpdated) onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    }
  };

  const markPending = async (installmentId) => {
    if (!isAdmin) return;
    try {
      await api.patch(`/installments/${installmentId}`, { status: 'pending', paid_date: null });
      toast.success('Rata riaperta');
      await load();
      if (onUpdated) onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    }
  };

  if (loading) {
    return (
      <div className="rounded-lg border bg-[var(--surface-1)] p-4 text-xs text-muted-foreground">
        Caricamento piano pagamenti…
      </div>
    );
  }

  // Mostra la card SOLO se esiste un piano OPPURE lo stage è 'chiuso' (per editing)
  if (!plan && leadStage !== 'chiuso') {
    return null;
  }

  return (
    <div className="rounded-lg border bg-[var(--surface-1)] p-4" data-testid="payment-plan-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Wallet className="w-4 h-4 text-[hsl(var(--primary))]" />
          <div className="font-medium text-sm">Piano pagamenti</div>
          {plan && (
            <Badge variant="outline" className="text-xs">
              {plan.payment_type === 'rate' ? 'A rate' : 'Pagamento secco'}
            </Badge>
          )}
        </div>

        {!editing && (
          <Button size="sm" variant="outline" onClick={startEdit} data-testid="payment-plan-edit">
            <Pencil className="w-3 h-3 mr-1" /> {plan ? 'Modifica' : 'Imposta piano'}
          </Button>
        )}
      </div>

      {editing ? (
        <div className="space-y-3">
          <PaymentPlanEditor value={draft} onChange={setDraft} />
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={cancelEdit} disabled={saving}>
              <X className="w-3 h-3 mr-1" /> Annulla
            </Button>
            <Button onClick={saveDraft} disabled={saving} data-testid="payment-plan-save">
              <Save className="w-3 h-3 mr-1" /> {saving ? 'Salvataggio…' : 'Salva piano'}
            </Button>
          </div>
        </div>
      ) : plan ? (
        <>
          {/* Summary */}
          <div className="grid grid-cols-3 gap-2 mb-3 text-center">
            <div className="rounded bg-[var(--surface-2)] p-2">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Totale</div>
              <div className="text-sm font-semibold tabular-nums" data-testid="plan-total">
                {formatEUR(plan.total_amount)}
              </div>
            </div>
            <div className="rounded bg-emerald-50 p-2">
              <div className="text-[10px] uppercase tracking-wide text-emerald-700">Incassato</div>
              <div className="text-sm font-semibold tabular-nums text-emerald-800" data-testid="plan-paid">
                {formatEUR(plan.paid_total)}
              </div>
            </div>
            <div className="rounded bg-amber-50 p-2">
              <div className="text-[10px] uppercase tracking-wide text-amber-700">In attesa</div>
              <div className="text-sm font-semibold tabular-nums text-amber-800" data-testid="plan-pending">
                {formatEUR(plan.pending_total)}
              </div>
            </div>
          </div>

          {/* Installments */}
          <div className="space-y-1.5">
            {plan.installments.map((it) => (
              <div
                key={it.id}
                className="grid grid-cols-12 gap-2 items-center text-xs px-2 py-1.5 rounded hover:bg-[var(--surface-2)]"
                data-testid={`plan-installment-${it.installment_number}`}
              >
                <div className="col-span-1 text-muted-foreground">#{it.installment_number}</div>
                <div className="col-span-3 flex items-center gap-1">
                  <Calendar className="w-3 h-3 text-muted-foreground" />
                  <span className="tabular-nums">{shortDate(it.due_date)}</span>
                </div>
                <div className="col-span-3 text-right tabular-nums font-medium">{formatEUR(it.amount)}</div>
                <div className="col-span-3 truncate text-muted-foreground">{it.notes || ''}</div>
                <div className="col-span-2 flex justify-end">
                  {it.status === 'paid' ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50 h-7 px-2 text-xs"
                      onClick={() => markPending(it.id)}
                      disabled={!isAdmin}
                      data-testid={`installment-paid-${it.installment_number}`}
                    >
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Pagata
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-amber-700 hover:text-amber-800 hover:bg-amber-50 h-7 px-2 text-xs"
                      onClick={() => markPaid(it.id)}
                      disabled={!isAdmin}
                      data-testid={`installment-pending-${it.installment_number}`}
                    >
                      <Clock className="w-3 h-3 mr-1" /> In attesa
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {!isAdmin && (
            <div className="text-[10px] text-muted-foreground mt-2 flex items-center gap-1">
              <Lock className="w-3 h-3" /> Solo l&apos;amministrazione può marcare le rate come pagate.
            </div>
          )}
        </>
      ) : (
        <div className="text-xs text-muted-foreground">
          Nessun piano pagamenti impostato. Clicca &quot;Imposta piano&quot; per creare il piano (secco o a rate).
        </div>
      )}
    </div>
  );
}
